import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { TONE_BG, type Tone } from './tones';

/**
 * Flat card with the ink outline and a hard offset shadow (grape on lilac, ink in Sun mode).
 * Pass `onPress` to make the whole card one button that presses into its shadow.
 *
 *   <Card tone="butter" padding="lg">...</Card>
 *   <Card onPress={() => nav('/p/1')} aria-label="Open Giorgos">...</Card>
 */
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';
export type CardShadow = 'grape' | 'ink' | 'small' | 'none';

const PAD: Record<CardPadding, string> = { none: '', sm: 'p-3', md: 'p-4', lg: 'p-5' };
const SHADOW: Record<CardShadow, string> = {
  grape: 'shadow-hard',
  ink: 'shadow-ink',
  small: 'shadow-hard-sm',
  none: 'shadow-none',
};

export interface CardProps {
  children: ReactNode;
  tone?: Tone;
  padding?: CardPadding;
  shadow?: CardShadow;
  as?: 'div' | 'section' | 'article' | 'li' | 'aside';
  onPress?: () => void;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

const BASE = 'block rounded-tts border-(length:--tts-border) border-ink text-left';

export function Card({
  children,
  tone = 'white',
  padding = 'md',
  shadow = 'grape',
  as: As = 'div',
  onPress,
  className,
  ...aria
}: CardProps) {
  const cls = clsx(BASE, TONE_BG[tone], PAD[padding], SHADOW[shadow], className);
  if (onPress) {
    return (
      <button type="button" onClick={onPress} className={clsx(cls, 'tts-press w-full')} {...aria}>
        {children}
      </button>
    );
  }
  return (
    <As className={cls} {...aria}>
      {children}
    </As>
  );
}
